import type { KeyboardEvent } from 'react';

import { SentimentOverlay } from './SentimentOverlay';
import type { SentimentOverlayData } from './sentimentTypes';
import styles from './SeekBar.module.css';

interface SeekBarProps {
  currentTime: number;
  duration: number;
  onSeek: (seconds: number) => void;
  sentimentOverlay?: SentimentOverlayData | null;
}

function clampSeekTarget(target: number, duration: number) {
  return Math.min(Math.max(0, target), Math.max(0, duration));
}

export function SeekBar({
  currentTime,
  duration,
  onSeek,
  sentimentOverlay,
}: SeekBarProps) {
  const safeDuration = duration > 0 ? duration : 0;
  const safeCurrentTime = clampSeekTarget(currentTime, safeDuration);

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') {
      return;
    }

    event.preventDefault();
    const delta = event.shiftKey ? 10 : 5;
    const direction = event.key === 'ArrowRight' ? 1 : -1;
    onSeek(clampSeekTarget(safeCurrentTime + delta * direction, safeDuration));
  }

  return (
    <label className={styles.wrapper}>
      <span className={styles.track}>
        <span aria-hidden="true" className={styles.rail} />
        {sentimentOverlay ? <SentimentOverlay {...sentimentOverlay} /> : null}
        <input
          aria-label="Seek"
          className={styles.input}
          max={safeDuration}
          min={0}
          onChange={(event) => {
            onSeek(clampSeekTarget(Number(event.currentTarget.value), safeDuration));
          }}
          onKeyDown={handleKeyDown}
          step={1}
          type="range"
          value={safeCurrentTime}
        />
      </span>
    </label>
  );
}
